"use client";

import { useEffect, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "src/contexts/AuthContext";

interface AuthWrapperProps {
  children: React.ReactNode;
  unprotectedRoutes?: string[];
  adminRoutes?: string[];
}

function UnauthorizedContent() {
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-6">
              You don&apos;t have permission to access this page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AuthWrapper({ 
  children, 
  unprotectedRoutes = ["/login"],
  adminRoutes = ["/admin", "/admin/users", "/admin/settings"]
}: AuthWrapperProps) {
  const { user, isLoading, isAdmin } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Helper function to normalize paths by removing trailing slashes
  const normalizePath = (path: string) => path.replace(/\/$/, "");
  
  // Memoize normalized paths to prevent unnecessary recalculations
  const normalizedUnprotectedRoutes = useMemo(() => 
    unprotectedRoutes.map(normalizePath),
    [unprotectedRoutes]
  );

  const normalizedAdminRoutes = useMemo(() => 
    adminRoutes.map(normalizePath),
    [adminRoutes]
  );
  
  const normalizedCurrentPath = useMemo(() => 
    normalizePath(pathname),
    [pathname]
  );

  useEffect(() => {
    if (isLoading) return;

    // If no user and on protected route, redirect to login
    if (!user && !normalizedUnprotectedRoutes.includes(normalizedCurrentPath)) {
      router.push("/login");
    }
  }, [user, isLoading, normalizedUnprotectedRoutes, normalizedCurrentPath, router]);

  // If route is unprotected, render content
  if (normalizedUnprotectedRoutes.includes(normalizedCurrentPath)) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Check for admin route access
  const isAdminRoute = normalizedAdminRoutes.some(route => 
    normalizedCurrentPath.startsWith(route)
  );

  if (isAdminRoute && !isAdmin) {
    return <UnauthorizedContent />;
  }

  // If user is authenticated and has proper role access, render content
  if (user) {
    return children;
  }

  // This should never be reached due to redirects, but adding for safety
  return null;
} 